import React, { useState } from 'react'
import { Head } from '@inertiajs/react'
import { Check, MessageCircle, Download, ShieldCheck, Stethoscope, CalendarDays, Clock } from 'lucide-react'
import SignaturePad from '../components/SignaturePad'
import FinancePanel from '../components/FinancePanel'
import { hexFor, labelFor } from '../lib/conditionColors'

// Phase 2B — the PUBLIC, patient-facing interactive case page (rendered at /c/:token via the bare
// application layout, no dashboard chrome). PHI-minimised: first name only, no account/other family.
// Accepting records agreement ONLY — it never creates an invoice. All submits use plain fetch (the
// intake-409 lesson), not the Inertia router.

function ScanCard({ scan }) {
  return (
    <div className="overflow-hidden rounded-xl border border-brand-border bg-white">
      <div className="border-b border-brand-border px-3 py-2 text-sm font-medium text-brand-ink">{scan.label || 'Scan'}</div>
      <div className="relative bg-black">
        <img src={scan.image_url} alt={scan.label || 'scan'} className="block max-h-[60vh] w-full object-contain" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {(scan.annotations || []).map((m, i) => {
            const gx = (m.geometry?.x ?? 0) * 100
            const gy = (m.geometry?.y ?? 0) * 100
            const hex = hexFor(m.condition)
            return (
              <g key={i}>
                <circle cx={gx} cy={gy} r="1.8" fill={hex} fillOpacity="0.35" stroke={hex} strokeWidth="0.7" vectorEffect="non-scaling-stroke" />
                <circle cx={gx} cy={gy} r="0.5" fill={hex} vectorEffect="non-scaling-stroke" />
              </g>
            )
          })}
        </svg>
      </div>
      {(scan.annotations || []).length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 py-2">
          {[...new Set(scan.annotations.map((a) => a.condition))].map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-brand-ink">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: hexFor(c) }} /> {labelFor(c)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default function PublicCasePage({ token, practice = {}, patient = {}, estimate = {}, visits = [], scans = [], wa_booking_link, pdf_url, finance }) {
  const [accepted, setAccepted] = useState(!!estimate.accepted)
  const [signedName, setSignedName] = useState('')
  const [signature, setSignature] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // In-app booking — drives the SAME proven journey booker server-side (propose available times, then
  // book the chosen slot through the guarded BookingEngine, linked to this estimate). slots: null = not
  // fetched yet; [] = fetched, none free. booked: { label } once a slot is reserved.
  const [slots, setSlots] = useState(null)
  const [minutes, setMinutes] = useState(null)
  const [chosen, setChosen] = useState(null)
  const [planning, setPlanning] = useState(false)
  const [booking, setBooking] = useState(false)
  const [booked, setBooked] = useState(null)
  const [bookError, setBookError] = useState(null)

  const postJson = async (path, body) => {
    const csrf = (typeof document !== 'undefined' && document.querySelector('meta[name="csrf-token"]')?.content) || ''
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body || {}), credentials: 'same-origin',
    })
    return { ok: res.ok, data: await res.json().catch(() => ({})) }
  }

  const proposeTimes = async () => {
    setPlanning(true); setBookError(null)
    try {
      const { data } = await postJson(`/c/${token}/propose_schedule`, {})
      if (data.ok) { setSlots(data.slots || []); setMinutes(data.minutes || null) }
      else setBookError(data.error || 'Sorry — we couldn’t load times just now. Please try again.')
    } catch (e) {
      setBookError('Sorry — we couldn’t reach the practice. Please check your connection and try again.')
    }
    setPlanning(false)
  }

  const book = async () => {
    if (!chosen) return
    setBooking(true); setBookError(null)
    try {
      const { data } = await postJson(`/c/${token}/book`, { date: chosen.date, time: chosen.time })
      if (data.ok) { setBooked({ label: data.label || chosen.label }); window.scrollTo(0, 0) }
      else {
        setBookError(data.error || 'That time wasn’t available — please pick another.')
        // A just-taken / too-soon slot: refresh the list so the patient sees live availability.
        if (['slot_taken', 'already_booked', 'too_soon'].includes(data.status)) { setChosen(null); proposeTimes() }
        setBooking(false)
      }
    } catch (e) {
      setBookError('Sorry — we couldn’t reach the practice. Please try again.')
      setBooking(false)
    }
  }

  // A typed FULL name (first + last) is a valid electronic signature on its own; the drawn
  // signature is optional (accessibility — older patients struggle to draw on a phone).
  const canAccept = signedName.trim().split(/\s+/).length >= 2
  const accept = async () => {
    setError(null)
    if (!canAccept) { setError('Please enter your full name to accept.'); return }
    setSubmitting(true)
    try {
      const csrf = (typeof document !== 'undefined' && document.querySelector('meta[name="csrf-token"]')?.content) || ''
      const res = await fetch(`/c/${token}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrf,
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ signed_name: signedName.trim(), signature_data: signature }),
        credentials: 'same-origin',
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        setAccepted(true)
        window.scrollTo(0, 0)
      } else {
        setError(data.error || 'Sorry — we couldn’t record that. Please try again.')
        setSubmitting(false)
      }
    } catch (e) {
      setError('Sorry — we couldn’t reach the practice. Please check your connection and try again.')
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-surface/40 py-6 px-4">
      <Head title={`${estimate.title || 'Treatment plan'} — ${practice.name || 'Dr Chalita le Roux'}`} />
      <div className="mx-auto max-w-2xl space-y-5">

        {/* Header */}
        <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 text-brand-primary">
            <Stethoscope size={18} />
            <span className="text-sm font-semibold">{practice.name || 'Dr Chalita le Roux'}</span>
          </div>
          <h1 className="mt-2 text-xl font-semibold text-brand-ink">Hi {patient.first_name || 'there'}</h1>
          <p className="mt-1 text-sm text-brand-muted">
            Here is {estimate.title || 'your treatment plan'}. Take your time to look through it below.
          </p>
        </div>

        {accepted && !booked && (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
              <Check size={18} /> Thank you — your acceptance has been recorded.
            </div>
            <p className="mt-1 text-sm text-emerald-800">The next step is to pick a time — just tap <b>Show me available times</b> below and choose what suits you.</p>
            {wa_booking_link && (
              <a href={wa_booking_link} target="_blank" rel="noopener noreferrer"
                 className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 sm:w-auto">
                <MessageCircle size={16} /> Prefer WhatsApp? Message us
              </a>
            )}
          </div>
        )}

        {/* Plain-language summary */}
        {estimate.summary && (
          <div className="rounded-2xl border border-brand-border bg-white p-5 text-sm leading-relaxed text-brand-ink shadow-sm">
            {estimate.summary}
          </div>
        )}

        {/* Scans */}
        {scans.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-brand-ink">Your scans</h2>
            {scans.map((s) => <ScanCard key={s.study_id} scan={s} />)}
          </div>
        )}

        {/* Phased plan */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-brand-ink">The plan</h2>
          {visits.map((v) => (
            <div key={v.visit} className="rounded-2xl border border-brand-border bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-brand-ink">Visit {v.visit}{v.minutes ? <span className="ml-2 font-normal text-brand-muted">· about {v.minutes} min</span> : null}</span>
                <span className="text-sm font-semibold text-brand-ink">{v.subtotal}</span>
              </div>
              <ul className="space-y-1.5">
                {v.lines.map((l, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-brand-ink">{l.description}{l.tooth_number ? <span className="text-brand-muted"> · tooth {l.tooth_number}</span> : null}</span>
                    <span className="whitespace-nowrap text-brand-muted">{l.amount}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {estimate.discount ? (
            <div className="flex items-center justify-between rounded-2xl border border-brand-border bg-white p-4 shadow-sm">
              <span className="text-sm font-semibold text-brand-ink">Discount{estimate.discount_label ? ` (${estimate.discount_label})` : ''}</span>
              <span className="text-sm font-semibold text-brand-ink">− {estimate.discount}</span>
            </div>
          ) : null}
          <div className="flex items-center justify-between rounded-2xl border border-brand-primary/40 bg-white p-4 shadow-sm">
            <span className="text-base font-semibold text-brand-ink">{estimate.discount ? 'Total payable' : 'Total'}</span>
            <span className="text-base font-semibold text-brand-ink">{estimate.total}</span>
          </div>
        </div>

        {finance && <FinancePanel finance={finance} />}

        {/* Book your visit — IN-APP (primary path). Drives the same proven journey booker server-side:
            real duration, provider-locked to the patient's own dentist, guarded double-book check, and
            the appointment lands linked to this estimate. wa.me stays available as a secondary option. */}
        {booked ? (
          <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-5 shadow-sm">
            <div className="flex items-center gap-2 text-base font-semibold text-emerald-800">
              <Check size={20} /> You’re booked in!
            </div>
            <p className="mt-1 text-sm text-emerald-800">
              We’ve reserved <b>{booked.label}</b> for you. We’ll send a confirmation and remind you closer to the time. Need to change it? Just message us on WhatsApp.
            </p>
            {wa_booking_link && (
              <a href={wa_booking_link} target="_blank" rel="noopener noreferrer"
                 className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100">
                <MessageCircle size={16} /> Message us on WhatsApp
              </a>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-brand-primary/40 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-brand-ink">Book your visit</h2>
            <p className="mt-1 text-sm text-brand-muted">Pick a time that suits you and we’ll reserve it — no need to call or type anything.</p>
            {!slots && (
              <button onClick={proposeTimes} disabled={planning}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50">
                <CalendarDays size={16} /> {planning ? 'Finding times…' : 'Show me available times'}
              </button>
            )}
            {slots && slots.length > 0 && (
              <div className="mt-3">
                <p className="mb-2 text-xs text-brand-muted">Tap a time{minutes ? `, then confirm — about ${minutes} min` : ', then confirm'}.</p>
                <div className="flex flex-col gap-2">
                  {slots.map((s) => {
                    const active = chosen && chosen.date === s.date && chosen.time === s.time
                    return (
                      <button key={`${s.date}T${s.time}`} type="button" onClick={() => { setChosen(s); setBookError(null) }}
                              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition ${active ? 'border-brand-primary bg-brand-surface font-semibold text-brand-primary' : 'border-brand-border text-brand-ink hover:bg-brand-surface'}`}>
                        <Clock size={15} className="shrink-0" /> {s.label}
                      </button>
                    )
                  })}
                </div>
                {bookError && <p className="mt-2 text-sm text-red-600">{bookError}</p>}
                <button onClick={book} disabled={!chosen || booking}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
                  <Check size={16} /> {booking ? 'Booking…' : (chosen ? 'Confirm this time' : 'Pick a time above')}
                </button>
                <p className="mt-2 text-center text-xs text-brand-muted">
                  None of these suit?{' '}
                  {wa_booking_link && <a className="font-semibold text-brand-primary" href={wa_booking_link} target="_blank" rel="noreferrer">Chat to us on WhatsApp</a>}
                </p>
              </div>
            )}
            {slots && slots.length === 0 && (
              <div className="mt-3 rounded-lg border border-brand-border bg-brand-surface/40 p-3 text-sm text-brand-muted">
                We couldn’t find an online time just now.{' '}
                {wa_booking_link && <a className="font-semibold text-brand-primary" href={wa_booking_link} target="_blank" rel="noreferrer">Message us on WhatsApp</a>} and we’ll arrange it with you.
              </div>
            )}
          </div>
        )}

        {/* Medical-aid framing (approved) */}
        <div className="flex items-start gap-2 rounded-xl border border-brand-border bg-white p-4 text-sm text-brand-muted">
          <ShieldCheck size={18} className="mt-0.5 shrink-0 text-brand-primary" />
          <p>Please note we do <strong className="text-brand-ink">not</strong> claim from medical aid — you pay the practice and claim it back from your medical aid yourself. We’ll give you everything you need for your claim.</p>
        </div>

        {/* Accept + e-sign — booking IS agreement, so once they've booked we don't ask them to accept too. */}
        {!accepted && !booked && (
          <div className="rounded-2xl border border-brand-border bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-brand-ink">Happy to go ahead?</h2>
            <p className="mt-1 text-xs text-brand-muted">Accepting records that you’re happy with the plan. It does not charge you anything — payment is arranged with the practice at your visit.</p>
            <div className="mt-3 space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold text-brand-muted">Your full name</label>
                <input value={signedName} onChange={(e) => setSignedName(e.target.value)} className="w-full rounded-md border border-brand-border px-3 py-2 text-sm" placeholder="Type your full name" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-brand-muted">Signature <span className="font-normal text-brand-muted">(optional)</span></label>
                <SignaturePad onChange={setSignature} />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                onClick={accept}
                disabled={submitting || !canAccept}
                title={canAccept ? undefined : 'Enter your full name to accept'}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Check size={16} /> {submitting ? 'Recording…' : 'Accept this plan'}
              </button>
            </div>
          </div>
        )}

        {/* Booking + PDF */}
        <div className="flex flex-col gap-2 sm:flex-row">
          {wa_booking_link && (
            <a href={wa_booking_link} target="_blank" rel="noopener noreferrer"
               className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90">
              <MessageCircle size={16} /> Book on WhatsApp
            </a>
          )}
          {pdf_url && (
            <a href={pdf_url} target="_blank" rel="noopener noreferrer"
               className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-brand-border bg-white px-4 py-2.5 text-sm font-semibold text-brand-ink transition hover:bg-brand-surface">
              <Download size={16} /> Download PDF
            </a>
          )}
        </div>

        {/* Trust + validity footer — mirrors the PDF letterhead so a high-value plan carries verifiable credentials */}
        <div className="space-y-1 pb-6 pt-1 text-center text-xs text-brand-muted">
          <p className="font-medium text-brand-ink">{practice.name || 'Dr Chalita le Roux'}</p>
          {practice.address && <p>{practice.address}</p>}
          <p>
            {practice.hpcsa && <>HPCSA: {practice.hpcsa}</>}
            {practice.practice_no && <> · Practice no: {practice.practice_no}</>}
            {practice.vat && <> · VAT: {practice.vat}</>}
          </p>
          {(practice.phone || practice.email) && <p>{practice.phone}{practice.phone && practice.email ? ' · ' : ''}{practice.email}</p>}
          <p className="pt-1">
            This is an estimate of planned treatment — actual fees may vary with what’s clinically needed on the day.
            {estimate.valid_until ? ` Valid until ${estimate.valid_until}.` : ' Valid for 14 days.'}
          </p>
          {estimate.number && <p className="text-brand-muted/70">Reference: {estimate.number}</p>}
        </div>
      </div>
    </div>
  )
}
